/**
 * AI Controller - Main Application
 */

import React, { Suspense, useState, useEffect, useMemo, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useControllerStatus } from './hooks/useFirebaseData';
import { NAV_SECTIONS } from '../shared/routePermissions';
import { getDefaultRoute } from '../shared/routePermissions';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { lazyWithReload } from './utils/lazyWithReload';

// Pages — all lazy so each route is its own chunk. lazyWithReload reloads the
// tab once if a chunk 404s after a deploy (stale manifest in a long-lived tab).
const ControllerDashboard = lazyWithReload(() => import('./pages/ControllerDashboard').then(m => ({ default: m.ControllerDashboard })));
const DevicesPage = lazyWithReload(() => import('./pages/DevicesPage').then(m => ({ default: m.DevicesPage })));
const RulesPage = lazyWithReload(() => import('./pages/RulesPage').then(m => ({ default: m.RulesPage })));
const IOMappingPage = lazyWithReload(() => import('./pages/IOMappingPage').then(m => ({ default: m.IOMappingPage })));
const LogsPage = lazyWithReload(() => import('./pages/LogsPage').then(m => ({ default: m.LogsPage })));
const SettingsPage = lazyWithReload(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const AnalyticsPage = lazyWithReload(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const UserManagementPage = lazyWithReload(() => import('./pages/UserManagementPage').then(m => ({ default: m.UserManagementPage })));
const AiAgentsPage = lazyWithReload(() => import('./pages/AiAgentsPage').then(m => ({ default: m.AiAgentsPage })));
const RoomPage = lazyWithReload(() => import('./pages/RoomPage').then(m => ({ default: m.RoomPage })));
const ClimatePage = lazyWithReload(() => import('./pages/ClimatePage').then(m => ({ default: m.ClimatePage })));
const AlertsPage = lazyWithReload(() => import('./pages/AlertsPage').then(m => ({ default: m.AlertsPage })));
const SensorsPage = lazyWithReload(() => import('./pages/SensorsPage').then(m => ({ default: m.SensorsPage })));
const IrrigationPage = lazyWithReload(() => import('./pages/IrrigationPage').then(m => ({ default: m.IrrigationPage })));
const SchedulesPage = lazyWithReload(() => import('./pages/SchedulesPage').then(m => ({ default: m.SchedulesPage })));
const FacilityPage = lazyWithReload(() => import('./pages/FacilityPage').then(m => ({ default: m.FacilityPage })));
const RoomsPage = lazyWithReload(() => import('./pages/RoomsPage').then(m => ({ default: m.RoomsPage })));
const BatchIntegrationPage = lazyWithReload(() => import('./pages/BatchIntegrationPage').then(m => ({ default: m.BatchIntegrationPage })));
const RecipePresetsPage = lazyWithReload(() => import('./pages/RecipePresetsPage').then(m => ({ default: m.RecipePresetsPage })));
const HVACDashboard = lazyWithReload(() => import('./pages/HVACDashboard').then(m => ({ default: m.HVACDashboard })));
const AccessDeniedPage = lazyWithReload(() => import('./pages/AccessDeniedPage').then(m => ({ default: m.AccessDeniedPage })));

// Components
import { OfflineBanner } from './components/OfflineBanner';
import LoadTimeout from './components/LoadTimeout';
import { useLoadGate } from './hooks/useLoadGate';
import { NotificationProvider, useNotifications } from './context/NotificationContext';
import { WatchdogProvider } from './context/WatchdogContext';
import { AlertsBanner } from './components/alerts/AlertsBanner';
import { NotificationToastContainer } from './components/notifications/NotificationToast';

import { useTheme } from './context/ThemeContext';
import { useDevicePrefs } from './utils/devicePrefs';

import {
  LayoutDashboard,
  Thermometer,
  Sprout,
  Settings,
  Menu,
  ChevronDown,
  Sliders,
  LogOut,
} from 'lucide-react';
import {
  WorkspaceSwitcher,
  visibleApps,
  type AppRegistry,
  type VisibleAppsContext,
} from '@/vendor/elevia-ui/react';
import registryJson from '@/vendor/elevia-ui/apps.json';
import { useLabPickerContext, type PickerTables } from './hooks/useLabPickerContext';

/**
 * App picker ("Switch workspace"): every Elevia app this user can open, from their Cultivation role
 * (useLabPickerContext). Until the role is read, or if it cannot be, only the Controller tile shows
 * with a link to Cultivation's picker. The Controller tile points at this origin, so the Minix LAN
 * copy never sends the operator to the cloud copy.
 */
const LAB_APP_PICKER_URL = 'https://app.elevia.cz/app-select';
const REGISTRY = registryJson as unknown as AppRegistry & PickerTables;
function launcherApps(ctx: VisibleAppsContext) {
  const apps = REGISTRY.apps.map(app =>
    app.id === 'controller' ? { ...app, url: window.location.origin } : app
  );
  return visibleApps(apps, ctx);
}

const SECTION_ICONS: Record<string, React.ReactNode> = {
  overview: <LayoutDashboard size={20} />,
  climate: <Thermometer size={20} />,
  control: <Sliders size={20} />,
  cultivation: <Sprout size={20} />,
  system: <Settings size={20} />,
};

const SECTION_ROUTES: Record<string, string[]> = {
  overview: ['/', '/facility', '/rooms', '/analytics'],
  climate: ['/climate', '/hvac', '/sensors'],
  control: ['/devices', '/rules', '/schedules', '/io-mapping'],
  cultivation: ['/irrigation', '/batch-integration', '/batches', '/recipe-presets'],
  system: ['/alerts', '/logs', '/settings', '/users', '/ai-agents'],
};

/**
 * Z-INDEX SCALE (keep all layered UI on this scale):
 *   z-20  sticky in-page elements (alerts banner, table headers)
 *   z-30  mobile top bar
 *   z-40  drawer backdrop
 *   z-50  sidebar drawer
 *   z-[60]  floating chat widget
 *   z-[100] modals / dialogs
 *   z-[110] toasts / notifications
 */

export default function App() {
  const {
    isAuthenticated, isLoading, hasControllerAccess, profileLoadFailed, login, logout, firebaseUser, refreshProfile,
  } = useAuth();

  // Auth boot gate: the profile getDoc can hang forever on a stalled network.
  // Timeout offers a manual retry; it never auto-denies access.
  const bootGate = useLoadGate(12000);
  const { markLoaded: bootMarkLoaded, retry: bootRetry } = bootGate;
  const authResolvedOnce = useRef(false);
  if (!isLoading) {
    authResolvedOnce.current = true;
  }
  useEffect(() => {
    if (isLoading) {
      bootRetry();
    } else {
      bootMarkLoaded();
    }
  }, [isLoading, bootMarkLoaded, bootRetry]);

  const awaitingSession = !isAuthenticated || !firebaseUser;
  if (awaitingSession) {
    // After auth has resolved once, never swap the sign-in form for
    // LoadingScreen — that remount drops typed credentials and the error.
    if (isLoading && !authResolvedOnce.current) {
      if (bootGate.timedOut) {
        return (
          <div className="h-dvh overflow-hidden flex items-center justify-center bg-background text-text">
            <LoadTimeout
              label="Can't reach the server"
              onRetry={() => {
                bootRetry();
                refreshProfile().catch(() => {});
              }}
            />
          </div>
        );
      }
      return <LoadingScreen message="Loading..." />;
    }
    return <LoginScreen onLogin={login} />;
  }

  // Profile fetch FAILED (offline / stalled network): access is unknown, not
  // denied. Offer retry instead of the access-denied page (never auto-deny).
  if (profileLoadFailed) {
    return (
      <div className="h-dvh overflow-hidden flex items-center justify-center bg-background text-text">
        <LoadTimeout
          label="Can't reach the server"
          onRetry={() => {
            refreshProfile().catch(() => {});
          }}
        />
      </div>
    );
  }

  if (!hasControllerAccess) {
    return (
      <BrowserRouter>
        <Suspense fallback={<LoadingScreen message="Loading..." />}>
          <Routes>
            <Route path="*" element={<AccessDeniedPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <NotificationProvider>
        <WatchdogProvider>
          <AppLayout onLogout={logout} />
        </WatchdogProvider>
      </NotificationProvider>
    </BrowserRouter>
  );
}

function AppLayout({ onLogout }: { onLogout: () => void }) {
  const { status: controllerStatus } = useControllerStatus();
  const { can, firebaseUser } = useAuth();
  const { theme } = useTheme();
  const pickerCtx = useLabPickerContext(firebaseUser ?? null, REGISTRY);
  const pickerApps = useMemo(() => launcherApps(pickerCtx), [pickerCtx]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const {
    notifications: notifs,
    unreadCount,
    acknowledgeNotification,
    acknowledgeNotifications,
    acknowledgeAllActive,
  } = useNotifications();

  const navigate = useNavigate();
  const defaultRoute = getDefaultRoute(can);
  const { alertsBannerEnabled } = useDevicePrefs();

  const activeAlerts = useMemo(() => {
    return notifs
      .filter(n => !n.acknowledged && (n.severity === 'warning' || n.severity === 'critical'))
      .map(n => ({
        id: n.id,
        severity: n.severity as 'warning' | 'critical',
        title: n.title,
        message: n.message,
        timestamp: n.timestamp,
        roomId: n.roomId,
        roomName: n.data?.roomName as string | undefined,
        acknowledged: n.acknowledged,
      }));
  }, [notifs]);

  const visibleSections = useMemo(() => {
    return NAV_SECTIONS.map(section => ({
      ...section,
      items: section.items.filter(item => can(item.category, item.action)),
    })).filter(section => section.items.length > 0);
  }, [can]);

  // Close the drawer whenever the route changes (mobile navigation)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Current page title for the mobile top bar (longest matching nav item)
  const pageTitle = useMemo(() => {
    let best: { label: string; len: number } | null = null;
    for (const section of visibleSections) {
      for (const item of section.items) {
        const match = item.to === '/'
          ? location.pathname === '/'
          : location.pathname.startsWith(item.to);
        if (match && (!best || item.to.length > best.len)) {
          best = { label: item.label, len: item.to.length };
        }
      }
    }
    return best?.label ?? 'Elevia';
  }, [visibleSections, location.pathname]);

  const isOnline = controllerStatus?.status === 'online';

  return (
    <div className="flex flex-col h-dvh w-full overflow-hidden bg-background text-text">
      <OfflineBanner />

      <div className="flex flex-1 flex-row w-full min-h-0 relative">
        {/* Drawer backdrop (compact viewports only) */}
        <div
          onClick={() => setSidebarOpen(false)}
          aria-hidden
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${
            sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        />

        {/* Sidebar: persistent on lg+, overlay drawer below */}
        <aside
          className={`w-72 max-w-[85vw] shrink-0 flex flex-col fixed lg:static top-0 left-0 bottom-0 z-50
            bg-[var(--ev-surface-sidebar)] backdrop-blur-2xl border-r border-[var(--ev-border-hairline)]
            transition-transform duration-300 ease-out lg:translate-x-0 pt-safe ${
              sidebarOpen ? 'translate-x-0 shadow-glass-elevated' : '-translate-x-full lg:shadow-none'
            }`}
        >
          <div className="px-5 py-5 border-b border-[var(--ev-border-hairline)]">
            <div className="flex items-center gap-3">
              <img src="/logo-elevia.png" alt="Elevia" className="h-9 w-auto shrink-0" />
              <span className="text-lg font-semibold tracking-[-0.01em] text-[var(--ev-text-strong)] leading-none">
                Elevia Controller
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-2.5">
              <span className={`status-led !w-2 !h-2 ${isOnline ? 'status-led-healthy' : 'status-led-critical'}`} />
              <span className="text-sm text-text-muted">
                {isOnline ? 'Online' : 'Offline'}
                {controllerStatus?.hardware && ` • ${controllerStatus.hardware}`}
              </span>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar py-3 px-3">
            <NavAccordionProvider>
              <ul className="list-none p-0 m-0 flex flex-col gap-1">
                {visibleSections.map(section => (
                  <NavSection
                    key={section.id}
                    id={section.id}
                    title={section.title}
                    icon={SECTION_ICONS[section.id]}
                  >
                    {section.items.map(item => (
                      <NavItem
                        key={item.to}
                        to={item.to}
                        label={item.label}
                        exact={item.exact}
                        badge={item.to === '/alerts' ? unreadCount : undefined}
                      />
                    ))}
                  </NavSection>
                ))}
              </ul>
            </NavAccordionProvider>
          </nav>

          <div className="p-4 border-t border-[var(--ev-border-hairline)] pb-safe">
            <div className="flex items-center justify-between gap-2 mb-3 px-2.5">
              <span className="text-sm text-text-muted truncate min-w-0">
                {firebaseUser?.email}
              </span>
            </div>
            <WorkspaceSwitcher
              label="Switch workspace"
              current="controller"
              apps={pickerApps}
              className="mb-1"
              launcherProps={pickerApps.length > 1 ? undefined : {
                description: (
                  <>
                    Other Elevia apps: <a href={LAB_APP_PICKER_URL}>open the Elevia app picker</a>
                  </>
                ),
              }}
            />
            <button
              onClick={onLogout}
              className="ev-nav-item"
            >
              <LogOut size={15} />
              Sign Out
            </button>
          </div>
        </aside>

        <div className="flex-1 flex flex-col min-w-0 min-h-0 relative z-0">
          {/* Mobile top bar */}
          <header className="lg:hidden sticky top-0 z-30 shrink-0 flex items-center gap-3 px-3 pt-safe
            bg-background backdrop-blur-xl border-b border-[var(--ev-border-hairline)]">
            <div className="flex items-center gap-3 h-14 w-full">
              <button
                onClick={() => setSidebarOpen(true)}
                aria-label="Open navigation"
                className="w-12 h-12 rounded-full flex items-center justify-center text-text bg-transparent
                  border-none cursor-pointer transition-colors hover:bg-surface-hover active:scale-95"
              >
                <Menu size={22} />
              </button>
              <span className="text-base font-semibold text-text truncate">{pageTitle}</span>
              <span className="flex-1" />
              <span className={`status-led ${isOnline ? 'status-led-healthy' : 'status-led-critical'}`} />
            </div>
          </header>

          <main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar pb-safe px-safe">
            {alertsBannerEnabled && activeAlerts.length > 0 && (
              <AlertsBanner
                alerts={activeAlerts}
                onAcknowledge={acknowledgeNotification}
                onAcknowledgeMany={acknowledgeNotifications}
                onAcknowledgeAll={acknowledgeAllActive}
                onViewAll={() => navigate('/alerts')}
              />
            )}

            {/* key={theme} remounts pages on theme switch so charts/canvas re-read CSS vars */}
            <Suspense fallback={<PageLoadingFallback />}>
            <Routes key={theme}>
            <Route path="/" element={<ProtectedRoute><ControllerDashboard /></ProtectedRoute>} />
            <Route path="/facility" element={<ProtectedRoute><FacilityPage /></ProtectedRoute>} />
            <Route path="/monitoring" element={<Navigate to="/settings?tab=system" replace />} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/ai" element={<Navigate to="/irrigation" replace />} />
            <Route path="/climate" element={<ProtectedRoute><ClimatePage /></ProtectedRoute>} />
            <Route path="/climate/:roomId" element={<ProtectedRoute><ClimatePage /></ProtectedRoute>} />
            <Route path="/hvac" element={<ProtectedRoute><HVACDashboard /></ProtectedRoute>} />
            <Route path="/sensors" element={<ProtectedRoute><SensorsPage /></ProtectedRoute>} />
            <Route path="/system" element={<Navigate to="/settings?tab=system" replace />} />
            <Route path="/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
            <Route path="/devices" element={<ProtectedRoute><DevicesPage /></ProtectedRoute>} />
            <Route path="/rules" element={<ProtectedRoute><RulesPage /></ProtectedRoute>} />
            <Route path="/schedules" element={<ProtectedRoute><SchedulesPage /></ProtectedRoute>} />
            <Route path="/io-mapping" element={<ProtectedRoute><IOMappingPage /></ProtectedRoute>} />
            <Route path="/irrigation" element={<ProtectedRoute><IrrigationPage /></ProtectedRoute>} />
            <Route path="/irrigation/:roomId" element={<ProtectedRoute><IrrigationPage /></ProtectedRoute>} />
            <Route path="/batches" element={<Navigate to="/batch-integration" replace />} />
            <Route path="/batch-integration" element={<ProtectedRoute><BatchIntegrationPage /></ProtectedRoute>} />
            <Route path="/recipe-presets" element={<ProtectedRoute><RecipePresetsPage /></ProtectedRoute>} />
            <Route path="/rooms" element={<ProtectedRoute><RoomsPage /></ProtectedRoute>} />
            <Route path="/rooms/:roomId" element={<ProtectedRoute><RoomPage /></ProtectedRoute>} />
            <Route path="/rooms/:roomId/climate" element={<ProtectedRoute><ClimatePage /></ProtectedRoute>} />
            <Route path="/rooms/:roomId/irrigation" element={<ProtectedRoute><IrrigationPage /></ProtectedRoute>} />
            <Route path="/logs" element={<ProtectedRoute><LogsPage /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>} />
            <Route path="/ai-agents" element={<ProtectedRoute><AiAgentsPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/access-denied" element={<AccessDeniedPage />} />
              <Route path="*" element={<Navigate to={defaultRoute} replace />} />
            </Routes>
            </Suspense>
          </main>
        </div>

        {/* Info-severity toasts (warning/critical are owned by the AlertsBanner) */}
        <NotificationToastContainer />
      </div>
    </div>
  );
}

const NavAccordionContext = React.createContext<{
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
}>({ expandedId: null, setExpandedId: () => {} });

function NavAccordionProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [expandedId, setExpandedId] = useState<string | null>(() => {
    for (const [sectionId, routes] of Object.entries(SECTION_ROUTES)) {
      if (routes.some(r => r === '/' ? location.pathname === '/' : location.pathname.startsWith(r))) {
        return sectionId;
      }
    }
    return null;
  });

  useEffect(() => {
    for (const [sectionId, routes] of Object.entries(SECTION_ROUTES)) {
      if (routes.some(r => r === '/' ? location.pathname === '/' : location.pathname.startsWith(r))) {
        setExpandedId(sectionId);
        return;
      }
    }
  }, [location.pathname]);

  const value = useMemo(() => ({ expandedId, setExpandedId }), [expandedId]);

  return (
    <NavAccordionContext.Provider value={value}>
      {children}
    </NavAccordionContext.Provider>
  );
}

function NavSection({ id, title, icon, children }: { id: string; title: string; icon: React.ReactNode; children: React.ReactNode }) {
  const { expandedId, setExpandedId } = React.useContext(NavAccordionContext);
  const location = useLocation();
  const isExpanded = expandedId === id;
  const isGroupActive = (SECTION_ROUTES[id] ?? []).some(
    r => r === '/' ? location.pathname === '/' : location.pathname.startsWith(r)
  );

  return (
    <li>
      <button
        onClick={() => setExpandedId(isExpanded ? null : id)}
        className={`tap:min-h-12 w-full flex items-center justify-between px-3 py-3 rounded-[10px] border-none cursor-pointer
          text-base font-semibold leading-5 text-left transition-colors duration-200 ${
            isExpanded || isGroupActive
              ? 'text-text bg-[var(--ev-accent-tint)] [&_svg]:text-[var(--ev-accent-icon)]'
              : 'text-text-muted bg-transparent hover:text-text hover:bg-surface-hover'
          }`}
      >
        <span className="flex items-center gap-3">
          {icon}
          <span>{title}</span>
        </span>
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${
            isExpanded || isGroupActive ? 'opacity-80' : 'opacity-50'
          }`}
        />
      </button>
      {isExpanded && (
        <ul className="list-none p-0 m-0 mt-1 mb-2 flex flex-col gap-0.5">
          {children}
        </ul>
      )}
    </li>
  );
}

function NavItem({ to, label, badge, exact }: { to: string; label: string; badge?: number; exact?: boolean }) {
  const location = useLocation();
  const isActive = exact
    ? location.pathname === to
    : (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  return (
    <li>
      <NavLink
        to={to}
        end={exact}
        className={`flex items-center justify-between py-2.5 pr-3 pl-11 rounded-[10px] no-underline
          text-[15px] leading-5 transition-colors duration-200 relative ${
            isActive
              ? 'text-text font-semibold bg-[var(--ev-accent-tint)]'
              : 'text-text-muted font-normal hover:text-text hover:bg-surface-hover'
          }`}
      >
        {isActive && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[var(--ev-accent-bar)]" />
        )}
        <span>{label}</span>
        {badge !== undefined && badge > 0 && (
          <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-danger text-[var(--ev-status-danger-on-fill)] text-xs font-bold
            flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </NavLink>
    </li>
  );
}

function PageLoadingFallback() {
  return (
    <div className="flex-1 flex items-center justify-center py-20">
      <div className="w-8 h-8 border-[3px] border-border border-t-primary rounded-full animate-spin" />
    </div>
  );
}

function LoadingScreen({ message }: { message: string }) {
  return (
    <div className="h-dvh overflow-hidden flex items-center justify-center bg-background text-text">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-border border-t-primary rounded-full animate-spin mx-auto mb-4" />
        <p className="text-text-muted">{message}</p>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }: { onLogin: (email: string, password: string) => Promise<void> }) {
  const { error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const shownError = error || authError || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onLogin(email, password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ev-login">
      <div className="ev-login__card">
        <div className="ev-login__head">
          <h1 className="ev-title">AI Controller</h1>
          <p className="ev-muted ev-login__subtitle">Sign in with your Elevia Lab account</p>
        </div>
        <form onSubmit={handleSubmit} noValidate className="ev-login__form">
          <div className="ev-field">
            <label className="ev-field__label">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="ev-input" placeholder="you@elevialab.com" required />
          </div>
          <div className="ev-field">
            <label className="ev-field__label">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="ev-input" placeholder="••••••••" required />
          </div>
          {shownError && (
            <p data-testid="login-error" className="ev-banner bg-[var(--ev-status-danger-soft)] border-danger/25">{shownError}</p>
          )}
          <button type="submit" disabled={loading} className="ev-btn ev-btn--primary ev-btn--block">
            {loading ? 'Authenticating...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
